import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { gql } from '../../../graphql/client';
import {
  M_ADD_ACTION,
  M_CREATE_VIEW_INTERACTION,
  M_DELETE_ACTION,
  M_DELETE_VIEW_INTERACTION,
  M_UPDATE_ACTION,
  M_UPDATE_INTERACTION_TYPE,
  Q_VIEW_INTERACTIONS,
} from '../../../graphql/operations';
import type { View, ViewInteraction } from '../../../types/forge';
import { ACTION_TYPES, ACTION_TYPE_KEYS, TRIGGER_TYPES } from './vocabulary';

interface AppDataCacheShape {
  View?: Pick<View, 'id' | 'Type' | 'name'>[];
}

/**
 * Right-panel "Interactions" tab — Phase 5.
 *
 * Per selected view: list its interactions (each a trigger + ordered
 * actions). Authored inline via dropdowns + text fields.
 *
 * Persistence rhythm:
 *  - "Add interaction" / "Add action" / "Delete" → fire mutation immediately
 *  - Trigger-type dropdown edit → fire mutation on change
 *  - Action.Type / Action.Value edits → buffered locally; explicit "Save"
 *    button per action commits.
 *
 * After every mutation we invalidate both the per-view interactions query
 * AND the broader app-data query so the snapshot picker (PackagingControls)
 * sees fresh data on the next "Create package".
 */
export function InteractionsForm({ viewId, appId }: { viewId: string; appId: string }) {
  const queryClient = useQueryClient();

  const interactions = useQuery({
    queryKey: ['view-interactions', viewId],
    queryFn: () =>
      gql<{ View_Interaction: ViewInteraction[] }>(Q_VIEW_INTERACTIONS, { viewId }),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['view-interactions', viewId] });
    queryClient.invalidateQueries({ queryKey: ['app-data', appId] });
  };

  const [pendingType, setPendingType] = useState<string>(TRIGGER_TYPES[0]);

  const createInteraction = useMutation({
    mutationFn: () => gql(M_CREATE_VIEW_INTERACTION, { viewId, type: pendingType }),
    onSuccess: invalidate,
  });

  const updateTriggerType = useMutation({
    mutationFn: ({ id, type }: { id: string; type: string }) =>
      gql(M_UPDATE_INTERACTION_TYPE, { id, type }),
    onSuccess: invalidate,
  });

  const deleteInteraction = useMutation({
    mutationFn: ({ viInId, iId }: { viInId: string; iId: string }) =>
      gql(M_DELETE_VIEW_INTERACTION, { viewInteractionId: viInId, interactionId: iId }),
    onSuccess: invalidate,
  });

  if (interactions.isLoading) return <p style={s.empty}>Loading interactions…</p>;
  if (interactions.error)
    return (
      <p style={s.error}>
        Failed to load interactions: {(interactions.error as Error).message}
      </p>
    );

  const rows = interactions.data?.View_Interaction ?? [];

  // Pull Screen names from the cached app-data so the "Navigate to Screen"
  // value field can render a dropdown. The parent BuilderScreen has
  // already populated this cache; reading it here is a no-op fetch.
  const appData = queryClient.getQueryData<AppDataCacheShape>(['app-data', appId]);
  const appScreenNames = (appData?.View ?? [])
    .filter((v) => v.Type === 'Screen' && typeof v.name === 'string' && v.name.length > 0)
    .map((v) => v.name);

  return (
    <div style={s.root}>
      {rows.length === 0 && (
        <p style={s.empty}>No interactions yet. Add one below.</p>
      )}

      {rows.map((vi) => (
        <div key={vi.id} style={s.interactionCard}>
          <div style={s.cardHeader}>
            <select
              value={vi.interaction.type}
              onChange={(e) =>
                updateTriggerType.mutate({ id: vi.interaction_id, type: e.target.value })
              }
              style={s.triggerSelect}
            >
              {TRIGGER_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() =>
                deleteInteraction.mutate({ viInId: vi.id, iId: vi.interaction_id })
              }
              style={s.deleteBtn}
              title="Delete interaction"
            >
              ×
            </button>
          </div>

          <ActionsList
            interactionId={vi.interaction_id}
            actions={vi.interaction.Interaction_Actions}
            onChanged={invalidate}
            appScreenNames={appScreenNames}
          />
        </div>
      ))}

      <div style={s.addBar}>
        <select
          value={pendingType}
          onChange={(e) => setPendingType(e.target.value)}
          style={s.triggerSelect}
        >
          {TRIGGER_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => createInteraction.mutate()}
          disabled={createInteraction.isPending}
          style={s.addInteractionBtn}
        >
          {createInteraction.isPending ? 'Adding…' : '+ Add interaction'}
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

interface ActionRowProps {
  ia: ViewInteraction['interaction']['Interaction_Actions'][number];
  onChanged: () => void;
  appScreenNames: string[];
}

function ActionsList({
  interactionId,
  actions,
  onChanged,
  appScreenNames,
}: {
  interactionId: string;
  actions: ViewInteraction['interaction']['Interaction_Actions'];
  onChanged: () => void;
  appScreenNames: string[];
}) {
  const addAction = useMutation({
    mutationFn: () => {
      const nextPriority =
        actions.length === 0 ? 0 : Math.max(...actions.map((a) => a.priority)) + 1;
      return gql(M_ADD_ACTION, {
        interactionId,
        priority: nextPriority,
        type: 'Open URL',
      });
    },
    onSuccess: onChanged,
  });

  return (
    <div style={s.actionsList}>
      {actions.length === 0 && (
        <p style={s.empty}>No actions yet — add one to make this interaction do something.</p>
      )}
      {actions.map((ia) => (
        <ActionRow
          key={ia.id}
          ia={ia}
          onChanged={onChanged}
          appScreenNames={appScreenNames}
        />
      ))}
      <button
        type="button"
        onClick={() => addAction.mutate()}
        disabled={addAction.isPending}
        style={s.addActionBtn}
      >
        {addAction.isPending ? 'Adding…' : '+ Add action'}
      </button>
    </div>
  );
}

function ActionRow({ ia, onChanged, appScreenNames }: ActionRowProps) {
  const [type, setType] = useState(ia.Action.Type);
  const [value, setValue] = useState(ia.Action.Value ?? '');
  const [dirty, setDirty] = useState(false);

  // Reset local buffer when the underlying action changes (e.g. after
  // refetch invalidates and reconciles).
  useEffect(() => {
    setType(ia.Action.Type);
    setValue(ia.Action.Value ?? '');
    setDirty(false);
  }, [ia.Action.id, ia.Action.Type, ia.Action.Value]);

  const save = useMutation({
    mutationFn: () => gql(M_UPDATE_ACTION, { id: ia.action_id, type, value }),
    onSuccess() {
      setDirty(false);
      onChanged();
    },
  });

  const del = useMutation({
    mutationFn: () =>
      gql(M_DELETE_ACTION, {
        interactionActionId: ia.id,
        actionId: ia.action_id,
      }),
    onSuccess: onChanged,
  });

  const meta = ACTION_TYPES[type];

  return (
    <div style={s.actionCard}>
      <div style={s.cardHeader}>
        <select
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setDirty(true);
          }}
          style={s.actionTypeSelect}
        >
          {ACTION_TYPE_KEYS.map((k) => (
            <option key={k} value={k}>
              {ACTION_TYPES[k]?.label ?? k}
            </option>
          ))}
        </select>
        <span style={s.priority} title="Priority — lower runs first">
          p{ia.priority}
        </span>
        <button
          type="button"
          onClick={() => del.mutate()}
          style={s.deleteBtn}
          title="Delete action"
        >
          ×
        </button>
      </div>

      {meta?.valueInput === 'screen-name' ? (
        appScreenNames.length > 0 ? (
          <select
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setDirty(true);
            }}
            style={s.input}
          >
            <option value="">— pick a screen —</option>
            {appScreenNames.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        ) : (
          <input
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setDirty(true);
            }}
            placeholder="Screen name (no Screens defined yet)"
            style={s.input}
          />
        )
      ) : (
        <input
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setDirty(true);
          }}
          placeholder="Value"
          style={s.input}
        />
      )}
      {meta && <p style={s.hint}>{meta.valueHint}</p>}
      {meta?.status === 'logs-only' && (
        <p style={s.statusLogs}>
          Engine logs this action today; full execution lands in a follow-up.
        </p>
      )}

      {dirty && (
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          style={s.saveBtn}
        >
          {save.isPending ? 'Saving…' : 'Save action'}
        </button>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

const s: Record<string, React.CSSProperties> = {
  root: { display: 'flex', flexDirection: 'column', gap: 12 },
  empty: { fontSize: 12, color: '#94A3B8' },
  error: { fontSize: 12, color: '#991B1B' },
  interactionCard: {
    padding: 10,
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    backgroundColor: '#F8FAFC',
  },
  cardHeader: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 },
  triggerSelect: {
    flex: 1,
    padding: '6px 8px',
    fontSize: 12,
    fontWeight: 600,
    color: '#0F172A',
    border: '1px solid #CBD5E1',
    borderRadius: 4,
    backgroundColor: '#fff',
  },
  actionTypeSelect: {
    flex: 1,
    padding: '4px 6px',
    fontSize: 11,
    border: '1px solid #CBD5E1',
    borderRadius: 4,
    backgroundColor: '#fff',
  },
  priority: {
    fontSize: 10,
    fontFamily: 'monospace',
    color: '#94A3B8',
    padding: '2px 4px',
  },
  deleteBtn: {
    width: 22,
    height: 22,
    padding: 0,
    fontSize: 14,
    lineHeight: '22px',
    color: '#94A3B8',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
  },
  actionsList: { display: 'flex', flexDirection: 'column', gap: 6 },
  actionCard: {
    padding: 8,
    border: '1px solid #E2E8F0',
    borderRadius: 6,
    backgroundColor: '#fff',
  },
  input: {
    width: '100%',
    padding: '6px 8px',
    fontSize: 12,
    border: '1px solid #CBD5E1',
    borderRadius: 4,
    boxSizing: 'border-box',
  },
  hint: { fontSize: 10, color: '#94A3B8', margin: '4px 0 0' },
  statusLogs: {
    fontSize: 10,
    color: '#A16207',
    backgroundColor: '#FEFCE8',
    padding: '2px 6px',
    borderRadius: 4,
    margin: '4px 0 0',
  },
  saveBtn: {
    marginTop: 6,
    padding: '4px 10px',
    fontSize: 11,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#4F46E5',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    alignSelf: 'flex-start',
  },
  addActionBtn: {
    padding: '4px 8px',
    fontSize: 11,
    fontWeight: 500,
    color: '#4F46E5',
    backgroundColor: 'transparent',
    border: '1px dashed #C7D2FE',
    borderRadius: 4,
    cursor: 'pointer',
  },
  addBar: { display: 'flex', gap: 6 },
  addInteractionBtn: {
    padding: '6px 10px',
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#4F46E5',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
  },
};
